import type { IAssetFileQuality, IAssetTag, IAssetProperty } from '~/types/assets-management'
import type { IAssetFile } from '~/types/file'

import { qualityIdentifierOptions, QUALITY_IDENTIFIER, statusOptions } from '~/const/asset'
import { ASSET_STATUS } from '~/types/assets-management'

/**
 * This arrow function takes a Dynamic Screen asset file, assigns its id and quality_identifier to a new object of type IAssetFileQuality, and returns that object.
 * @param IAssetFile
 * @return IAssetFileQuality
 */
export const fileToFileQuality = ({ id, quality_identifier }: IAssetFile): IAssetFileQuality => ({
  file_id: id,
  qualifier: quality_identifier as QUALITY_IDENTIFIER,
})

export const getQualityIdentifierFromFilename = (filename: string): QUALITY_IDENTIFIER => {
  let qi = QUALITY_IDENTIFIER.DEFAULT_ANDROID

  for (const { value } of qualityIdentifierOptions) {
    if (filename.includes(value)) {
      qi = value as QUALITY_IDENTIFIER
    }
  }

  return qi
}

export const getSingleValueTags = (tags: IAssetTag[]): IAssetTag[] => {
  return tags.reduce((prev: IAssetTag[], { asset_id, key, value: values }) => {
    values.forEach((value) => {
      prev.push({ asset_id, key, value: [value] })
    })
    return prev
  }, [])
}

export const getStatusOptions = (status?: ASSET_STATUS): IOption[] => {
  let allowedStatus: ASSET_STATUS[] = []

  switch (status) {
    case ASSET_STATUS.DRAFT:
      allowedStatus = [ASSET_STATUS.DRAFT_ABORTED, ASSET_STATUS.DRAFT, ASSET_STATUS.RELEASED]
      break
    case ASSET_STATUS.DRAFT_ABORTED:
      allowedStatus = [ASSET_STATUS.DRAFT_ABORTED, ASSET_STATUS.DRAFT]
      break
    case ASSET_STATUS.RELEASED:
      allowedStatus = [ASSET_STATUS.RELEASED, ASSET_STATUS.STOPPED, ASSET_STATUS.DEPRECATED]
      break
    case ASSET_STATUS.STOPPED:
      allowedStatus = [ASSET_STATUS.STOPPED, ASSET_STATUS.DEPRECATED, ASSET_STATUS.RELEASED]
      break
    case ASSET_STATUS.DEPRECATED:
      allowedStatus = [ASSET_STATUS.STOPPED, ASSET_STATUS.DEPRECATED, ASSET_STATUS.RELEASED]
  }

  return statusOptions.filter(({ value }) => allowedStatus.includes(value as ASSET_STATUS))
}

export const getTagsArray = (properties: IAssetProperty[]): Omit<IAssetTag, 'asset_id'>[] => {
  const reduceProperty = (prev: Omit<IAssetTag, 'asset_id'>[], { key, value }: IAssetProperty) => {
    const duplicateIndex = prev.findIndex(({ key: prevKey }) => prevKey === key?.value)
    if (duplicateIndex != -1) {
      prev[duplicateIndex].value.push(value)
    } else {
      key && prev.push({ key: key.value, value: [value] })
    }
    return prev
  }

  return properties.reduce(reduceProperty, [])
}

export const getPropertiesArray = (tags: IAssetTag[]): IAssetProperty[] => {
  return tags.reduce((prev: IAssetProperty[], { key, value }: IAssetTag) => {
    value.forEach((singleValue) => {
      prev.push({ key: { label: key, value: key }, value: singleValue })
    })
    return prev
  }, [])
}

export const getKeysOptions = (keys?: string[]): IOption[] => {
  if (keys) {
    return keys.map((key) => {
      return { label: key, value: key }
    })
  } else {
    return []
  }
}
